import { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { NewLoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import PerText from '../../../components/custom_components/Text'
import { useCompanyManagerDashboardBoOfCompany } from '../../../graphql/company-manager-dashboard/hooks/useCompanyManagerDashboardBusinessUnitsAndBranchOfficesQueries'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { BranchOfficeDto, BranchOfficeWithEmployeesDto } from '../../../types'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { useAuth } from '../../../context/auth-context'
import { PerAvatarDashboardMonitoringTable } from '../../../components/custom_components/AvatarPlus/PerAvatarDashboardMonitoringTable'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { useDashboardStore } from '../DashboardStore'
import DashboardActiveFilterPill from './DashboardActiveFilterPill'

type DashboardDistributionsOfficeTabContentProps = {
  selectedCompanyId: string
}

const DashboardDistributionsOfficeTabContent: FC<DashboardDistributionsOfficeTabContentProps> = ({ selectedCompanyId }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const [appraisalMonitoringFilters, setAppraisalMonitoringFilters, selectedDashboardRoles] = useDashboardStore((state) => [
    state.appraisalMonitoringFilters,
    state.setAppraisalMonitoringFilters,
    state.selectedDashboardRoles,
  ])

  const {
    data: boOfCompanyData,
    loading: loadingBoOfCompany,
    error: boOfCompanyError,
  } = useCompanyManagerDashboardBoOfCompany(selectedCompanyId, selectedDashboardRoles)

  useEffect(() => {
    if (!boOfCompanyError) {
      return
    }
    showNotification({
      type: 'error',
      message: t('dashboard.companyManager.filtersArea.distributions.boOfCompanyQueryError.title'),
      description: t('dashboard.companyManager.filtersArea.distributions.boOfCompanyQueryError.description'),
    })
  }, [boOfCompanyError, t])

  const officeList: BranchOfficeWithEmployeesDto[] = boOfCompanyData?.companyManagerDashboardBoOfCompany.branchOfficesWithEmployees ?? []

  const allEmployees = officeList
    .flatMap(({ branchOffice, employees }) => employees.map((employee) => ({ employee, branchOffice })))
    .sort((a, b) => {
      const nameA = computeFullname(user.nameDisplayPreference, a.employee.firstName, a.employee.lastName)
      const nameB = computeFullname(user.nameDisplayPreference, b.employee.firstName, b.employee.lastName)
      return nameA.localeCompare(nameB)
    })
  const selectedBranchOffice: BranchOfficeDto | undefined = appraisalMonitoringFilters.selectedBranchOffice
  const displayedEmployees = selectedBranchOffice
    ? allEmployees.filter(({ branchOffice }) => branchOffice.id === selectedBranchOffice.id)
    : allEmployees
  const summaryEmployeeCount = displayedEmployees.length

  const onOfficeChipClick = (branchOffice: BranchOfficeDto) => {
    const turningOff = selectedBranchOffice?.id === branchOffice.id
    setAppraisalMonitoringFilters({
      ...appraisalMonitoringFilters,
      selectedBranchOffice: turningOff ? undefined : branchOffice,
    })
  }

  return (
    <div className="dashboard-filters-area__distributions__office-content">
      {loadingBoOfCompany || !!boOfCompanyError ? (
        <PerFlexRow justify="center" align="middle" className="fullHeight">
          <NewLoadingAndErrors
            t={t}
            isLoading={loadingBoOfCompany}
            hasNoData={false}
            showSpinner
            error={boOfCompanyError}
            iconSize={100}
            showOnlyIcon
          />
        </PerFlexRow>
      ) : (
        <>
          <div className="dashboard-filters-area__distributions__office-list">
            {officeList.map(({ branchOffice }) => {
              const isSelected = !!selectedBranchOffice && branchOffice.id === selectedBranchOffice.id
              return (
                <DashboardActiveFilterPill key={branchOffice.code} selected={isSelected} onClick={() => onOfficeChipClick(branchOffice)}>
                  {branchOffice.code}
                </DashboardActiveFilterPill>
              )
            })}
          </div>
          <PerFlexRow align="middle" mt="xxs">
            <PerText fontSize="s" color="secondary" fontWeight="bold">
              {summaryEmployeeCount}
            </PerText>
            <PerText color="secondary" fontSize="xs" ml="xxxs">
              {selectedBranchOffice
                ? selectedBranchOffice.id
                  ? t('dashboard.companyManager.filtersArea.distributions.officeList.countInSelectedOffice', {
                      officeName: officeList.find(({ branchOffice }) => branchOffice.id === selectedBranchOffice.id)!.branchOffice.code,
                    })
                  : t('dashboard.companyManager.filtersArea.distributions.officeList.countInNoOffice')
                : t('dashboard.companyManager.filtersArea.distributions.officeList.count')}
            </PerText>
          </PerFlexRow>

          <div className="dashboard-filters-area__distributions__office-employees-list">
            {displayedEmployees.map(({ employee, branchOffice }) => (
              <PerFlexRow
                key={employee.id}
                align="middle"
                direction="row"
                justify="space-between"
                width="fullWidth"
                noWrap
                className="dashboard-filters-area__distributions__office-employee-row"
              >
                <PerAvatarDashboardMonitoringTable
                  nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
                  employeeId={employee.id}
                  firstName={employee.firstName}
                  lastName={employee.lastName}
                  subtitle={t(`employee.contract.level.${employee.currentLevel}`)}
                />
                <PerText color="secondary" fontSize="xs" className="dashboard-filters-area__distributions__office-employee-office">
                  {branchOffice.code}
                </PerText>
              </PerFlexRow>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default DashboardDistributionsOfficeTabContent
